<template>
  <!-- Trigger Button -->
  <button
    @click="toggle()"
    aria-haspopup="listbox"
    :aria-controls="listboxId"
    class="flex items-center justify-between bg-white px-4 py-2 w-full rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary mb-8">
    <span class="text-sm font-medium text-gray-600"> Step {{ currentIndex + 1 }} of {{ sections.length }} </span>
    <svg
      class="h-5 w-5 text-gray-500 transform transition-transform duration-200"
      :class="{ 'rotate-180': isOpen }"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
    </svg>
  </button>

  <!-- Dropdown List -->
  <ul
    v-if="isOpen"
    :id="listboxId"
    role="listbox"
    tabindex="-1"
    class="mt-1 bg-white shadow-lg rounded-lg max-h-60 overflow-auto w-full focus:outline-none"
    @keydown.escape.prevent="close()">
    <li
      v-for="(sec, i) in sections"
      :key="sec.id"
      role="option"
      class="px-4 py-2 flex items-center justify-between text-sm space-x-2 cursor-pointer transition-colors duration-150"
      :class="itemClasses(i)"
      @click="handleNavigation(i)"
      @keydown.enter.prevent="handleNavigation(i)"
      tabindex="0">
      <span class="font-semibold">{{ i + 1 }}.</span>
      <span v-if="completed.includes(i)" class="text-green-500">✓</span>
      <span>{{ sec.title }}</span>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import type { Section } from "../models/Section";

interface Props {
  sections: Section[];
  currentIndex: number;
  completed: number[];
}
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "navigate", idx: number): void;
}>();

// State
const isOpen = ref(false);
const listboxId = `dropdown-listbox-${Math.random().toString(36).slice(-6)}`;

// Methods
function toggle() {
  isOpen.value = !isOpen.value;
}
function close() {
  isOpen.value = false;
}
function handleNavigation(idx: number) {
  // Only allow up to one step ahead
  emit("navigate", idx);
  close();
}

// Dynamic classes
function itemClasses(i: number) {
  if (i === props.currentIndex) {
    return "bg-gradient-to-r from-primary to-secondary text-white";
  }
  if (props.completed.includes(i)) {
    return "bg-gray-100 text-gray-700 hover:bg-gray-200";
  }
  return "text-gray-500 hover:bg-gray-50";
}
</script>
